import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Box, useTheme } from "@mui/material";
import Button from "../../components/Button";
import AddIcon from "@mui/icons-material/Add";
import { tokens } from "../../assets/theme";
import { FormattedMessage, useIntl } from "react-intl";
import CircularProgress from "@mui/material/CircularProgress";
import Popup from "../../components/Popup";
import AxionControls from "../../components/controls/AxionControls";
import { pushErrorMessage } from "../../utils/notifications";
import { fetchMachineData } from "../../fetchers/machine";
import {
    getMachineSectionsForMachine,
    createMachineSection,
    updateMachineSection,
    deactivateMachineSection,
    setMachineSectionStopReasonCodes,
} from "../../fetchers/machineSections";
import MachineSectionsGrid from "./components/machineSectionsGrid";
import MachineSectionAddEditDialog from "./components/machineSectionAddEditDialog";
import MachineSectionStopCodesDialog from "./components/machineSectionStopCodesDialog";
import messages from "./components/messages";

const emptyMachineSection = (machineId) => ({
    id: null,
    machineId,
    name: "",
    code: "",
    sortOrder: 0,
    isActive: true,
});

// Opened as the content of a Popup from the machines list — machineId is fixed for the
// lifetime of the dialog, there is no machine picker here.
export default function MachineSections(props) {
    const { machineId, stopReasonCodes } = props;
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const { formatMessage } = useIntl();

    const [openPopup, setOpenPopup] = useState(false);
    const [machineSection, setMachineSection] = useState(emptyMachineSection(machineId));

    const [openStopCodesPopup, setOpenStopCodesPopup] = useState(false);
    const [stopReasonCodeIds, setStopReasonCodeIds] = useState([]);
    const [currentSectionId, setCurrentSectionId] = useState(null);

    const [showBackdrop, setShowBackdrop] = useState(false);

    const machineDataQuery = useQuery(
        ["machine", machineId],
        () => fetchMachineData(machineId),
        { enabled: !!machineId }
    );

    const machineSectionsQuery = useQuery(
        ["machineSections", machineId],
        () => getMachineSectionsForMachine(machineId),
        { enabled: !!machineId }
    );

    const availableStopReasonCodes = machineDataQuery.data
        ? (stopReasonCodes ?? []).filter((stopReasonCode) =>
            machineDataQuery.data.machineStopReasonCodes.some((x) => x.stopReasonCodeId === stopReasonCode.id))
        : [];

    const handleAddMachineSectionClick = () => {
        setMachineSection(emptyMachineSection(machineId));
        setOpenPopup(true);
    };

    const handleEditMachineSectionClick = (id) => {
        const section = machineSectionsQuery.data.find((x) => x.id === id);
        setMachineSection({ ...section, isActive: true });
        setOpenPopup(true);
    };

    const handleDeleteMachineSectionClick = (id) => {
        setShowBackdrop(true);
        deactivateMachineSection(id).then(() => {
            machineSectionsQuery.refetch();
            setShowBackdrop(false);
        }).catch((error) => {
            console.log(error);
            setShowBackdrop(false);
            pushErrorMessage(`Cannot delete the machine section.`);
        });
    };

    const handleChange = (event) => {
        const { id, name, value } = event.target;
        setMachineSection((prev) => ({
            ...prev,
            [id ? id : name]: value,
        }));
    };

    const handleSaveMachineSection = () => {
        if (!machineSection.name) {
            pushErrorMessage(formatMessage(messages.nameIsMandatory));
            return;
        }

        setShowBackdrop(true);

        const sectionToSave = {
            ...machineSection,
            sortOrder: Number(machineSection.sortOrder) || 0,
        };

        const savePromise = sectionToSave.id
            ? updateMachineSection(sectionToSave)
            : createMachineSection(sectionToSave);

        savePromise.then(() => {
            setOpenPopup(false);
            machineSectionsQuery.refetch();
            setShowBackdrop(false);
        }).catch((error) => {
            console.log(error);
            setShowBackdrop(false);
            pushErrorMessage(`Cannot save the machine section.`);
        });
    };

    const handleEditStopReasonCodesClick = (id) => {
        const section = machineSectionsQuery.data.find((x) => x.id === id);
        setCurrentSectionId(id);
        setStopReasonCodeIds(section.stopReasonCodeIds ?? []);
        setOpenStopCodesPopup(true);
    };

    const handleSaveStopReasonCodes = () => {
        setShowBackdrop(true);
        setMachineSectionStopReasonCodes(currentSectionId, stopReasonCodeIds).then(() => {
            setOpenStopCodesPopup(false);
            machineSectionsQuery.refetch();
            setShowBackdrop(false);
        }).catch((error) => {
            console.log(error);
            setShowBackdrop(false);
            pushErrorMessage(`Cannot save the stop codes for the machine section.`);
        });
    };

    if (machineDataQuery.isLoading || machineSectionsQuery.isLoading) {
        return <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}><CircularProgress color="secondary" /></div>;
    }

    return (
        <Box sx={{ position: "relative" }}>
            <Button
                text={<FormattedMessage {...messages.addMachineSectionTitle} />}
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={handleAddMachineSectionClick}
                sx={{
                    "&:hover": {
                        backgroundColor: colors.dashbordGreen[700],
                    },
                    backgroundColor: colors.dashbordGreen[500],
                    color: "#000000",
                    marginBottom: 2,
                }} />
            <MachineSectionsGrid
                isLoading={machineSectionsQuery.isLoading}
                machineSections={machineSectionsQuery.data ?? []}
                stopReasonCodes={stopReasonCodes ?? []}
                handleEditMachineSectionClick={handleEditMachineSectionClick}
                handleDeleteMachineSectionClick={handleDeleteMachineSectionClick}
                handleEditStopReasonCodesClick={handleEditStopReasonCodesClick}
            />
            <Popup
                openPopup={openPopup}
                setOpenPopup={setOpenPopup}
                maxWidth="sm"
                title={machineSection.id ? <FormattedMessage {...messages.editMachineSectionTitle} /> : <FormattedMessage {...messages.addMachineSectionTitle} />}>
                <MachineSectionAddEditDialog
                    onChange={handleChange}
                    handleSave={handleSaveMachineSection}
                    machineSection={machineSection}
                />
            </Popup>
            <Popup
                openPopup={openStopCodesPopup}
                setOpenPopup={setOpenStopCodesPopup}
                maxWidth="sm"
                title={<FormattedMessage {...messages.editStopReasonCodesTitle} />}>
                <MachineSectionStopCodesDialog
                    stopReasonCodeIds={stopReasonCodeIds}
                    onChange={setStopReasonCodeIds}
                    handleSave={handleSaveStopReasonCodes}
                    availableStopReasonCodes={availableStopReasonCodes}
                />
            </Popup>
            <AxionControls.AxionBackdrop open={showBackdrop} />
        </Box>
    );
}
